import { useEffect, useState } from "react";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import Board from "@publicPortal/components/board/Board";
import PASummaryTabContent from "./tabContent/PASummaryTabContent";
import { useParams } from "react-router-dom";
import PACommentsTabContent from "./tabContent/PACommentsTabContent";
import DocumentsTable from "@mirage/components/documentsTable/DocumentsTable";
import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import PlanningApplicationTabs from "./PlanningApplicationTabs";
import RemoveSaveBookmark from "@publicPortal/components/customBookmark/RemoveSaveBookmark";
import SaveBookmark from "@publicPortal/components/customBookmark/SaveBookmark";
import { UserService } from "@publicPortal/services/UserService";
import { TrackedApplicationService } from "@publicPortal/services/TrackedApplicationService";
import { IPlanningApplicationDetails } from "@shared/publicportal/contracts/IPlanningApplicationDetails";
import PlanningApplicationsService from "@publicPortal/services/PlanningApplicationsService";
import { VerifyCommentsDisabled } from "@shared/publicportal/helpers/VerifyCommentsDisabled";
import PAConstraintsTabContent from "./tabContent/PAConstraintsTabContent";
import PAConsultationsTabContent from "./tabContent/PAConsultationsTabContent";
import { PAMapTab } from "./tabContent/PAMapTab";
import { atom, useRecoilState } from "recoil";
import { RecordDetailsTabNames } from "@publicPortal/utils/enums";
import useAlertService from "@mirage/hooks/useAlertService";
import { IUser } from "@shared/mirage/core/contracts/IUser";

export const planningApplicationDetailsState = atom({
    key: "planningApplicationDetailsState",
    default: {} as { [x: string]: RecordDetailsTabNames },
});

const PlanningApplication = () => {
    const trackedApplicationService = new TrackedApplicationService();
    const planningAppService = new PlanningApplicationsService();
    const user: Partial<IUser> = UserService.getLoggedInUser();
    const { applicationId } = useParams();
    const [planningApp, setPlanningApp] = useState<IPlanningApplicationDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [isTracked, setIsTracked] = useState({ tracked: false, id: 0 });
    const [trackChanged, setTrackChanged] = useState(false);
    const [currentTab, setCurrentTab] = useRecoilState(planningApplicationDetailsState);
    const showAlert = useAlertService();

    useEffect(() => {
        getPlanningApp();
    }, []);

    useEffect(() => {
        getTracked();
    }, [trackChanged]);

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText={"Planning Application " + (planningApp?.name || "not found")} loadingHeaderText={loading}>
                <BackButton />
                {getBoardContent()}
            </Board>
        </>
    );

    function getBoardContent() {
        if (planningApp?.id) {
            return (
                <>
                    {getBookmark()}
                    <PlanningApplicationTabs
                        currentTab={currentTab[planningApp.id] || "Summary"}
                        handleChangeTab={(newTab) => {
                            setCurrentTab((prev) => ({ ...prev, [planningApp.id]: newTab }));
                        }}
                    />
                    {getTabContent(planningApp)}
                </>
            );
        }
    }

    function getTabContent(planningApp: IPlanningApplicationDetails) {
        switch (currentTab[planningApp.id] || "Summary") {
            case "Summary":
                return <PASummaryTabContent loading={loading} planningApp={planningApp} />;
            case "Constraints":
                return <PAConstraintsTabContent paId={planningApp.id} service={planningAppService} />;
            case "Documents":
                return (
                    <DocumentsTable
                        publicOnly={true}
                        readOnly
                        stateKey="planning-application-documents"
                        displayFor={{ entityRecord: { entityName: "P_APPLICATION", recordId: Number(applicationId) } }}
                    />
                );
            case "Map":
                return <PAMapTab planningApp={planningApp} />;
            case "Comments":
                return <PACommentsTabContent paId={planningApp.id} newCommentsDisabled={checkCommentsAreAllowed()} user={user} />;
            case "Consultations":
                return <PAConsultationsTabContent paId={planningApp.id} newCommentsDisabled={checkCommentsAreAllowed()} user={user} />;
            default:
                return <></>;
        }
    }

    function getBookmark() {
        if (isTracked.tracked) {
            return <RemoveSaveBookmark action={handleDeleteTracked} tooltipMessage="Remove Track" userId={user?.id} />;
        }
        return <SaveBookmark action={handleSaveTracked} tooltipMessage="Add Track" userId={user?.id} />;
    }

    function checkCommentsAreAllowed() {
        const commentsDisabled = VerifyCommentsDisabled(planningApp?.applicationStatus);
        return commentsDisabled;
    }

    async function handleSaveTracked() {
        await saveTrackedApplication();
        setTrackChanged((prev) => !prev);
    }

    async function handleDeleteTracked() {
        if (isTracked.id) {
            const result = await trackedApplicationService.deleteTrackedApplication(isTracked.id);
            if (result.errorMsg) {
                showAlert(result.errorMsg, "error");
            } else {
                showAlert("You successfully untracked application", "success");
            }
        }
        setTrackChanged((prev) => !prev);
    }

    async function getPlanningApp() {
        setLoading(true);
        const response = await planningAppService.getPlanningApplicationById(Number(applicationId));
        if (!response) {
            setLoading(false);
            return;
        }
        setPlanningApp(response);
        setLoading(false);
    }

    async function getTracked() {
        if (!user || !user.id) {
            return;
        }
        const result = await trackedApplicationService.getTracked();
        if (Array.isArray(result)) {
            const filteredApplication = result?.filter((tracked) => Number(tracked.paId) === Number(applicationId));
            if (filteredApplication.length) {
                setIsTracked({ tracked: true, id: filteredApplication[0].id });
                return;
            }
        }
        setIsTracked({ tracked: false, id: 0 });
    }

    async function saveTrackedApplication() {
        if (planningApp?.id) {
            const result = await trackedApplicationService.createTrackedApplication({ paId: planningApp.id });
            if (result?.errorMsg) {
                showAlert(result?.errorMsg, "error");
            } else {
                showAlert("You successfully tracked application", "success");
            }
        }
    }
};

export default PlanningApplication;
